import { Link, useSearchParams } from 'react-router';
import { useDashboard } from '@hooks/register/use-dashboard';
import { useLocalize } from '@hooks/use-localize';
import { useSession } from '@hooks/security/use-session';
import { DonorDashboardStep } from '@/types/dashboard';
import type { ReactNode } from 'react';
import type { IDashboardModel } from '@/types/dashboard';
import GuidancePanel from '@views/register/donor-dashboard-sections/guidance-panel';
import RuledOut from '@views/register/donor-dashboard-sections/ruled-out';
import Stepper from '@views/register/donor-dashboard-sections/stepper';

const DonorDashboard = () => {
    const [searchParams] = useSearchParams();
    const survey = searchParams.get('survey') || '';

    const { registrationId, editKey } = useSession();

    const { model, isLoading, error } = useDashboard(registrationId, editKey ?? '', survey);

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    return (
        <>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}

            <DonorDashboardContent model={model} />
        </>
    );
};

interface IDonorDashboardContentProps {
    model: IDashboardModel;
}

const DonorDashboardContent = ({ model }: IDonorDashboardContentProps) => {
    const t = useLocalize(model.languageData);

    const activeStep = model.currentStep;

    let body: ReactNode;

    if (model.isOptedOut) {
        body = (
            <div className="alert alert-danger">
                <div style={{ marginBottom: 10 }}>
                    {t(
                        'Alert_YouAreOptedOutOfOurAutomatedProcesses',
                        'You are opted out of our automated processes. This will eliminate most e-mails, but the transplant center may still contact you if your data has already been processed into the medical records database',
                    )}
                    .
                </div>
            </div>
        );
    } else if (model.isRuledOut) {
        body = (
            <>
                <Stepper model={model} t={t} activeStep={activeStep} />

                <RuledOut model={model} t={t} />
            </>
        );
    } else {
        body = (
            <>
                <Stepper model={model} t={t} activeStep={activeStep} />

                {activeStep === DonorDashboardStep.TransplantCenterWillContact && (
                    <div className="journey-panel">
                        {t(
                            'Alert_MakeEffortToAnswerUnknownNumbers',
                            'Please make every effort to answer unknown numbers while waiting for the transplant center to call you. The transplant center can typically only make 2 - 3 attempts before it is assumed you are no longer interested',
                        )}
                        .
                    </div>
                )}

                {model.isFiveStar && model.isOptedInForSurveys && model.currentStep < DonorDashboardStep.Completed ? (
                    <div className="journey-panel">
                        <div className="journey-panel__copy">
                            {t(
                                'Alert_WeWouldLikeToAskYouAboutYourExperience',
                                'We appreciate your time. At different points in the process, we would like to ask you about your experience at the transplant center so that we can work to improve donor experiences across the United States. These are simple surveys that should take less than 5 minutes to complete. If you do not wish to take part in the surveys, click the button below',
                            )}
                        </div>
                        <a className="journey-link-button" href="/register/donate?survey=false">
                            {t('DashLabel_IWishToOptOutOfSurveys', 'I wish to opt-out of the surveys')}
                        </a>
                    </div>
                ) : model.isFiveStar && !model.isOptedInForSurveys ? (
                    <div className="journey-panel">
                        <div className="journey-panel__copy">
                            {t(
                                'Alert_YouveOptedOutOfTheSurveyProcess',
                                "You've opted-out of the survey process. If you wish to change your mind and take part in the survey process, please",
                            )}{' '}
                            <a href="/register/donate?survey=true">
                                {t('DashLabel_ClickHere', 'click here')}
                            </a>
                        </div>
                    </div>
                ) : null}

                {model.offerToConnectToDonor && <GuidancePanel t={t} />}

                {model.currentStep < DonorDashboardStep.Completed ?
                    <div className='journey-opt-out'>
                        You may withdraw at any time using this link: <Link to={'/register/opt-out/' + model.id + '/' + model.editKey}>Opt-out of the donation process</Link>
                    </div>
                    :
                    <div className='journey-opt-out'>
                        Thank you for completing the donation process.
                    </div>
                }
            </>
        );
    }

    return (
        <div className="donation-journey">
            <h1 className="journey-title">{t('Dashboard_Title', 'Your Donation Dashboard')}</h1>
            <p className="journey-subtitle">
                {t(
                    'Dashboard_Description',
                    'Use this page to follow your progress and continue each step of the donation process',
                )}
                .
            </p>
            {body}
        </div>
    );
};

export default DonorDashboard;
